/* eslint-disable indent */
import _ from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { makeStyles, useMediaQuery, useTheme } from '~/lib/bn-material-ui';
import { BnDialog } from '~/common';
import { OrganizationPathName } from '~/organizations/organization-path-name';
import {
  useOrdersDialogDispatch,
  initialState,
  OrdersDialogContextProvider,
  validatedReducer,
  useOrdersDialogStateSelector,
  useOrdersDialogStateDispatchWrapper,
} from './context/orders-dialog-context';
import { BnErrorBoundary } from '~/common/bn-error-boundary';
import { initializeContext, initializeBarn } from './context/orders-dialog-context-initialize';
import { OrdersDialogContent } from './components/orders-dialog-content';
import { windowConfirmUnsavedChanges } from './context/orders-dialog-context-utilities';
import { useBnSnackbar } from '~/common/bn-snackbar-provider';

export function OrderPlannerDialog({ open, onClose, initialIsoDate, initialSelectedOrderId, initialBarnId, initialFeedId, initialBinAllocations, onOrdersUpdate, onOrderClose }) {
  // previously there were some issues with this code modifying the passed-in initial state, which would cause
  // poisoning between instances of the dialog. This is defence-in-depth to prevent that, but as far as we know, we have
  // eliminated existing cases where we were modifying the initial state.
  const clonedInitialState = useMemo(() => _.cloneDeep(initialState), []);
  return (
    <BnErrorBoundary locationTag='order-planner-dialog-outer'>
      <OrdersDialogContextProvider
        initialState={clonedInitialState}
        reducer={validatedReducer}
      >
        <OrdersDialogDisplay
          initialSelectedOrderId={initialSelectedOrderId}
          initialIsoDate={initialIsoDate}
          initialBarnId={initialBarnId}
          initialFeedId={initialFeedId}
          initialBinAllocations={initialBinAllocations}
          onOrdersUpdate={onOrdersUpdate}
          onOrderClose={onOrderClose}
          open={open}
          onClose={onClose}
        />
      </OrdersDialogContextProvider>
    </BnErrorBoundary>
  );
}

const useDialogDisplayStyles = makeStyles(() => ({
  titleClassName: {
    paddingTop: '0.35rem',
    paddingBottom: '.25rem',
  },
  ordersDialog: {
    minWidth: 'min(1044px, 100vw)',
  },
}));

function OrdersDialogDisplay({ open, onClose, onOrdersUpdate, onOrderClose, initialIsoDate, initialBarnId, initialSelectedOrderId, initialFeedId, initialBinAllocations }) {
  const [selectedBarnId, initializationState, submitting, hasUnsavedChanges, initializationErrorMessage, developerErrorMessage] = useOrdersDialogStateSelector([
    'selectedBarnId',
    'initializationState',
    'submitting',
    'hasUnsavedChanges',
    'initializationErrorMessage',
    'developerErrorMessage',
  ]);
  const { enqueueSnackbar } = useBnSnackbar();
  const dispatch = useOrdersDialogDispatch();
  const styles = useDialogDisplayStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const stateDispatchWrapper = useOrdersDialogStateDispatchWrapper();
  const doneInitializing = initializationState === 'complete';

  useEffect(
    function initializeBarnStore () {
      if (!initialBarnId) {
        return;
      }
      stateDispatchWrapper(initializeBarn)(
        { barnId: initialBarnId },
      );
    },
    [initialBarnId, stateDispatchWrapper],
  );

  const hasErrorMessage = initializationErrorMessage || developerErrorMessage;

  const onDialogClose = () => {
    dispatch({ type: 'clear-state' });
    onClose();
  };

  return (
    <BnDialog
      maxWidth='xl'
      open={open}
      fullScreen={fullScreen}
      classes={{ paper: styles.ordersDialog }}
      onClose={() => {
        if (hasUnsavedChanges && !windowConfirmUnsavedChanges()) {
          return;
        }
        onDialogClose();
      }}
      onOpen={() => {
        stateDispatchWrapper(initializeContext)(
          {
            initialIsoDate,
            initialSelectedOrderId,
            initialBarnId,
            initialFeedId,
            initialBinAllocations,
            onOrdersUpdate,
            onOrderClose,
            onDialogClose,
            enqueueSnackbar,
          },
        );
      }}
      title={<OrganizationPathName organizationHref={selectedBarnId} />}
      titleClassName={styles.titleClassName}
      overrideDialogActions={true}
      showProgressOverlay={!hasErrorMessage && (submitting || !doneInitializing)}
      className={styles.ordersDialog}
    >
      <BnErrorBoundary locationTag='order-planner-dialog-inner'>
        {hasErrorMessage ?
          <ErrorMessage
            developerErrorMessage={developerErrorMessage}
            initializationErrorMessage={initializationErrorMessage}
          /> :
          <OrdersDialogContent />
        }
      </BnErrorBoundary>
    </BnDialog>
  );
}

const useErrorStyles = makeStyles(({ palette }) => ({
  container: {
    padding: '1rem',
    background: palette.red[10],
  },
  title: {
    textAlign: 'center',
  },
}));

function ErrorMessage({ developerErrorMessage, initializationErrorMessage }) {
  const styles = useErrorStyles();
  return (
    <div className={styles.container}>
      <h3 className={styles.title}>{developerErrorMessage ? 'Developer Error' : 'Initialization Error'}</h3>
      <p>{developerErrorMessage || initializationErrorMessage}</p>
    </div>
  );
}
